import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { IField } from 'modules/core/types/common';
import { MODAL_DIALOG_ID, REQUEST_DELAY, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { useDebounceCallback, useIncomeReportButton, useModalState, useVehicleFormReset } from 'modules/core/hooks';
import { Button, CalculatorAdornment, FormRow, Panel, PanelWithFields } from 'modules/core/components';
import { ButtonVariant, ColumnWidth, DealSourceProvider, Roles, TableName } from 'modules/core/enums';
import { IncomeCalculator } from 'components/IncomeCalculator';
import { IIncomeForm } from 'components/IncomeCalculator/types';
import { CATEGORIES, FORM_FIELD_NAMES, TYPES } from 'components/IncomeCalculator/const';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { SettingsModal } from 'modules/ProbabilityEstimator/SettingsModal';
import { LendersMonthlyIncomeModal } from 'modules/RACAffordabilityEngine/RacMontlyIncomeModal/LendersMonthlyIncomeModal';
import { FEES_AND_TAXES_FIELD_NAMES } from 'modules/core/shared';
import { deleteUserVehicleThunk, fetchDealSourceDataThunk } from 'actions/vehicleActions';
import { getControlImagesThunk } from 'actions/controlActions';
import { useDealSourceData } from 'hooks/useDealSourceData';
import { getIsGamificationEnabled, getUser } from 'store/selectors/auth.selector';
import { useIsGamificationVisibleHere } from 'modules/core/hooks/useIsGamificationVisibleHere';
import { IUser, TFetchingVehicleFlagsSelector } from 'modules/core/types';
import { getIsModalsDisabled } from 'store/selectors/ui.selector';
import { getControlImages } from 'store/selectors/controls.selector';
import { ESTIMATED_FEES_OVERRIDDEN } from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import { getLenderState } from 'store/selectors/lenders.selector';
import { getRouteOneDealers } from 'store/selectors/dealer.selector';
import { FrontAndBackEndsAddsModal } from 'components/Modals/FrontAndBackEndsAddsModal';
import {
  ROUTE_ONE_DEALER_FIELD_NAME,
  ROUTE_ONE_SEARCH_FIELD_NAME,
  ROUTE_ONE_FILEDS_NAMES,
  ROUTE_ONE_DEALER_ID_FIELD_NAME,
  TIER_NAME,
} from 'modules/Lender/RehashCalculator/RehashCalculator.const';
import {
  APPROVED_LOAN_TERMS_FIELD_NAMES,
  COMMON_MONTHLY_INCOME_MODAL,
} from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { DisabledModals } from 'modules/core/enums/disabledModals';
import { IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG } from 'modules/auth/constants/auth.const';
import { routeOneAppFormatter } from 'modules/core/utils';
import { setIsModalsDisabled } from 'store/reducers/ui.reducer';
import {
  resetDealSourceData,
  resetDealSourceDataFlags,
  resetVehiclesByParams,
  resetVehiclesByStock,
} from 'store/reducers/lenders.reducer';
import { resetRACDealSourceRelatedData } from 'store/reducers/RACAffordabilityEngine.reducer';
import vehicleApi from 'api/vehicleApi';
import { getDeleteUserVehicleFlags } from 'store/selectors/vehicle.selector';
import { AchievementProgressCard, isCompletedToday } from 'components/Gamification';
import { getAchievements, getClosestAchievement } from 'store/selectors/gamification.selector';
import { fetchSummaryThunk } from 'actions/gamificationActions';
import { SSO_TYPE } from 'modules/core/constants';
import { LENDERS_MOTHLY_INCOME_DEFAULT_VALUES } from 'modules/RACAffordabilityEngine/RacMontlyIncomeModal/consts/defaultValues';
import { DefaultTooltip } from 'components/Modals/LoanVariablesDialog/components/common/styles';

import { PROBABILITY_ESTIMATOR_FIELDS } from '../probabilityEstimator.const';
import { MonthlyPaymentSlider } from '../MonthlyPaymentSlider';
import { NetDownPaymentModal } from './NetDownPaymentModal';
import {
  ConsumerInformationWrapper,
  HeaderButtonsGroup,
  RouteOneAppTooltip,
  ButtonIcon,
  SideImageContainer,
  LinkOutIconWrapper,
  LinkOutIcon,
  IncomeCalculatorIcon,
  OverrideFieldsWrapper,
} from './styles';
import { PageName, useConsumerInformationData } from './hooks/useConsumerInformationData';
import { DealSourceHeaderFields } from './DealSourceHeaderFields';

const matchesLender = (achievementLenderId: number | null, currentLenderDbId: number | null): boolean =>
  achievementLenderId === null || achievementLenderId === currentLenderDbId;

interface IConsumerInformation {
  title: string | ReactNode;
  firstConsumerFields: IField[];
  secondConsumerFields?: IField[];
  thirdConsumerFields?: IField[];
  isVehicleTableLoading?: boolean;
  pageName: PageName;
  totalVehicles?: number;
  submitCallback: VoidFunction;
  ltvTaxesFeesFilter?: string;
  areRequiredFieldsFilledIn: boolean;
  fetchingVehicleFlagsSelector: TFetchingVehicleFlagsSelector;
  tableName: TableName;
  settingsModalIsActive?: boolean;
  settingsModalHandleOpen?: VoidFunction;
  settingsModalDisabled?: boolean;
  isMakeItWork?: boolean;
  isRouteOneFlowForSbbOrSbc?: boolean;
  isShopByCallbackMode?: boolean;
  isShopByBudgetMode?: boolean;
  isDealSourceDataEmpty?: boolean;
  lenderHasRouteOneConfig?: boolean;
  isRouteOneFlow?: boolean;
  advancedOverridesFields?: IField[];
  cancelSubmitDebounced: Function;
}

export const ConsumerInformation: FC<IConsumerInformation> = ({
  title,
  isVehicleTableLoading,
  pageName,
  totalVehicles,
  submitCallback,
  ltvTaxesFeesFilter,
  firstConsumerFields,
  secondConsumerFields = [],
  thirdConsumerFields = [],
  areRequiredFieldsFilledIn,
  fetchingVehicleFlagsSelector,
  tableName,
  settingsModalIsActive = null,
  settingsModalHandleOpen = null,
  settingsModalDisabled = false,
  isMakeItWork = false,
  isRouteOneFlowForSbbOrSbc = false,
  isShopByCallbackMode = false,
  isShopByBudgetMode = false,
  isDealSourceDataEmpty = false,
  lenderHasRouteOneConfig = false,
  isRouteOneFlow = false,
  advancedOverridesFields,
  cancelSubmitDebounced,
}) => {
  const {
    getIncome,
    incomeCalculatorData,
    setIncome,
    resetVehicleFiltersDirtyFields,
    onResetState,
    initialValues,
  } = useConsumerInformationData(pageName);

  const { t } = useTranslation();
  const { onButtonPrintClick } = useIncomeReportButton(incomeCalculatorData);
  const { isSmallWidth } = useWindowDimensions();
  const dispatch = useDispatch();
  const isModalsDisabled = useSelector(getIsModalsDisabled);
  const user = useSelector(getUser) as IUser;
  const isGamificationEnabled = useSelector(getIsGamificationEnabled);
  const gamificationVisibleHere = useIsGamificationVisibleHere();
  const lenderState = useSelector(getLenderState);
  const controlImages = useSelector(getControlImages);
  const closestAchievement = useSelector(getClosestAchievement);
  const achievements = useSelector(getAchievements);
  const isRac = tableName === TableName.RAC;
  const isLender = tableName === TableName.LENDER;
  const sideImageLogo = isLender ? lenderState?.main_page_logo : isRac ? controlImages.racMainPageLogo : null;
  const currentLenderDbId = lenderState?.id ?? null;
  const hasApplicableAchievement =
    gamificationVisibleHere &&
    (achievements.some(
      (a) =>
        matchesLender(a.lender_id, currentLenderDbId) &&
        ((a.is_completed && isCompletedToday(a.completed_at)) || (!a.is_completed && a.current_count > 0)),
    ) ||
      (!!closestAchievement &&
        closestAchievement.current_count > 0 &&
        matchesLender(closestAchievement.lender_id, currentLenderDbId)));
  const showSideColumn = Boolean(sideImageLogo || hasApplicableAchievement);

  const sideImageLogoLink =
    (isLender && lenderState?.main_page_logo_link) || (isRac && controlImages.racMainPageLogoLink) || null;
  const sideImageLogoShouldDisplayUpgrade =
    ((isLender && lenderState?.main_page_logo_display_upgrade_inventory) ||
      (isRac && controlImages.racMainPageLogoDisplayUpgradeInventory)) &&
    !user.isWithInventory;

  const dealSourceContext = isLender ? TableName.LENDER : TableName.RAC;
  const { dealSourceData, dealSourceDataFlags } = useDealSourceData(dealSourceContext);
  const dealersList = useSelector(getRouteOneDealers);
  const [isSendRequest, setIsSendRequest] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [routeOneSearchCursorData, setRouteOneSearchCursorData] = useState<any>(undefined);
  const deleteUserVehicleFlags = useSelector(getDeleteUserVehicleFlags);
  const isLenderWithoutSSO = isLender && !user.is_sso && lenderHasRouteOneConfig;
  const isSalesperson = user?.role === Roles.SALESPERSON;
  const isDealerSsoType = user?.sso_type === SSO_TYPE.DEALER;
  const isDealerTrack = user?.dealSource === DealSourceProvider.DEALER_TRACK;
  const isSBBorSBCenabled = !!lenderState?.isShopByBudgetEnabled || !!lenderState?.isShopByCallbackEnabled;

  // On SBB+/SBC+ the DealerTrack dealer is already mapped from the Dealer ID received via SSO and the credit
  // app comes with the session, so both header fields are hidden and the page behaves like the standard flow.
  const isDealerTrackWithInventoryOnSbbSbc = isLender && isDealerTrack && !!user.isWithInventory && isSBBorSBCenabled;

  const toShowDealSourceSearchFields =
    !isDealerTrackWithInventoryOnSbbSbc &&
    ((isLenderWithoutSSO && !isSalesperson && !isDealerSsoType) ||
      ((isLender || isRac) && user.is_sso && !isDealerSsoType) ||
      (isDealerSsoType && lenderHasRouteOneConfig && isLender) ||
      (isDealerSsoType && isLender && isSBBorSBCenabled) ||
      (isRouteOneFlowForSbbOrSbc && !isSalesperson));

  const shouldDisableResetForLender = isLender && !isLenderWithoutSSO && (!isRouteOneFlowForSbbOrSbc || isSalesperson);
  const shouldDisableResetForRac = isRac && !!user?.is_sso;

  const isResetButtonDisabled =
    (isModalsDisabled[DisabledModals.REHASH_CALCULATOR] && (shouldDisableResetForRac || shouldDisableResetForLender)) ||
    isVehicleTableLoading;

  const {
    watch,
    formState: { isValid, errors },
    setValue,
    getValues,
    trigger,
  } = useFormContext();

  const isFormValid = Object.keys(errors).length === 0 && !isValid ? true : isValid;
  const values = getValues();

  const { resetInputs } = useVehicleFormReset(
    initialValues,
    onResetState,
    resetVehicleFiltersDirtyFields,
    ltvTaxesFeesFilter,
    isLender,
    false,
    lenderState?.guidebooks?.[0]?.internal_value,
  );

  const { resetAll } = useVehicleFormReset(
    initialValues,
    onResetState,
    resetVehicleFiltersDirtyFields,
    ltvTaxesFeesFilter,
    isLender,
    user.is_sso ? false : toShowDealSourceSearchFields,
    lenderState?.guidebooks?.[0]?.internal_value,
  );

  const {
    isModalDialogActive: isModalNetDownPaymentDialogActive,
    handleOpenDialog: handleNetDownPaymentDialogOpen,
  } = useModalState([MODAL_DIALOG_ID.NET_DOWN_PAYMENT]);

  const {
    isModalDialogActive: isFrontAndBackEndsAddsDialogActive,
    handleOpenDialog: handleFrontAndBackEndsAddsDialogOpen,
  } = useModalState([MODAL_DIALOG_ID.FRONT_AND_BACK_ENDS_ADDS]);

  const {
    isModalDialogActive: isIncomeDialogActive,
    handleOpenDialog: handleIncomeDialogOpen,
    handleCloseDialog: handleIncomeDialogClose,
  } = useModalState([MODAL_DIALOG_ID.INCOME_CALCULATOR]);

  const {
    isModalDialogActive: isLenderIncomeCalculatorDialogActive,
    handleOpenDialog: handleLenderIncomeCalculatorDialogOpen,
    handleCloseDialog: handleLenderIncomeCalculatorDialogClose,
  } = useModalState([MODAL_DIALOG_ID.LENDER_INCOME_CALCULATOR]);

  const { handleOpenDialog: handleUnlockInventoryDialogOpen } = useModalState([MODAL_DIALOG_ID.UNLOCK_INVENTORY]);

  const { isModalDialogActive: isModalLoanDialogActive } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES]);

  const hasBlockingModalOpen =
    isIncomeDialogActive ||
    isModalNetDownPaymentDialogActive ||
    isFrontAndBackEndsAddsDialogActive ||
    isLenderIncomeCalculatorDialogActive ||
    settingsModalIsActive;

  const handleMainPageLogoClick = useCallback(() => {
    if (sideImageLogoShouldDisplayUpgrade) {
      const unlockUrl = controlImages.unlockInventoryUrl;

      if (unlockUrl) {
        window.open(unlockUrl, '_blank');
      } else {
        handleUnlockInventoryDialogOpen();
      }
    } else if (sideImageLogoLink) {
      window.open(sideImageLogoLink, '_blank');
    }
  }, [
    sideImageLogoShouldDisplayUpgrade,
    sideImageLogoLink,
    handleUnlockInventoryDialogOpen,
    controlImages.unlockInventoryUrl,
  ]);

  const onIncomeCalculatorSubmit = useCallback(
    (incomeForm: IIncomeForm) => {
      dispatch(setIncome(incomeForm));

      if (isLender || isRac) {
        const currentLendersMonthlyIncome = getValues().lendersMonthlyIncomeModal;

        const coApplicantIncome = currentLendersMonthlyIncome?.income?.coApplicantMonthlyIncome || 0;
        const coApplicantOtherIncome = currentLendersMonthlyIncome?.income?.coApplicantOtherMonthlyIncome || 0;
        const totalIncomeWithCoApplicants = incomeForm.totalIncome + coApplicantIncome + coApplicantOtherIncome;

        setValue(COMMON_MONTHLY_INCOME_MODAL, {
          ...(currentLendersMonthlyIncome || {}),
          income: {
            ...(currentLendersMonthlyIncome?.income || {}),
            primaryMonthlyIncome: incomeForm.totalIncome,
            primaryOtherMonthlyIncome: LENDERS_MOTHLY_INCOME_DEFAULT_VALUES.income.primaryOtherMonthlyIncome,
            totalIncome: totalIncomeWithCoApplicants,
          },
        });
        setValue(PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME, totalIncomeWithCoApplicants);
      } else {
        setValue(PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME, incomeForm.totalIncome);
      }
    },
    [dispatch, setValue, setIncome, isLender, isRac, getValues],
  );

  useEffect(() => {
    if (cancelSubmitDebounced && hasBlockingModalOpen) {
      cancelSubmitDebounced();
    }
  }, [cancelSubmitDebounced, hasBlockingModalOpen]);

  const fetchRouteOneData = useCallback(async () => {
    const { routeOneSearch, routeOneDealer, routeOneDealerId } = getValues();
    const selectedRouteOneDealerId = user.isWithInventory ? routeOneDealer?.value : routeOneDealerId;

    if (!!selectedRouteOneDealerId && !!routeOneSearch && !deleteUserVehicleFlags.isFetching) {
      setIsSendRequest(true);
      const getRouteOneDataResponse: any = await dispatch(
        fetchDealSourceDataThunk({
          deal_source: user.dealSource ?? DealSourceProvider.ROUTE_ONE,
          conversation_id: routeOneAppFormatter(routeOneSearch),
          dealer_id: selectedRouteOneDealerId,
          lender_id: isLender ? lenderState?.id : null,
        }),
      );

      if (!axios.isCancel(getRouteOneDataResponse?.payload)) setIsDebouncing(false);
    }
  }, [
    getValues,
    user.isWithInventory,
    user.dealSource,
    deleteUserVehicleFlags.isFetching,
    dispatch,
    lenderState?.id,
    isLender,
  ]);

  const { debounced: handleGetRouteOneDataWithDelay } = useDebounceCallback((isCancel = false) => {
    if (!isCancel) fetchRouteOneData();
  }, REQUEST_DELAY);

  const dealSourceFieldHandleChange = useCallback(
    async (event) => {
      if (isModalLoanDialogActive) return;

      vehicleApi.cancelGetDealSourceData();
      setIsDebouncing(true);

      const isRouteOneDealerField = event?.target?.parentElement?.parentElement?.id === ROUTE_ONE_DEALER_FIELD_NAME;
      const isRouteOneInputField = [ROUTE_ONE_SEARCH_FIELD_NAME, ROUTE_ONE_DEALER_ID_FIELD_NAME].includes(
        event?.target?.name,
      );

      const getRouteOneDataFunction = isRouteOneInputField ? handleGetRouteOneDataWithDelay : fetchRouteOneData;

      if (isRouteOneDealerField) {
        handleGetRouteOneDataWithDelay(true);

        const routeOneDealer = getValues().routeOneDealer;

        if (routeOneDealer?.value && dealersList.length > 0) {
          const selectedDealer = dealersList.find((dealer) => dealer.route_one_id === routeOneDealer.value);

          if (selectedDealer) {
            setValue(FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE, selectedDealer.sales_tax_rate);
          }
        }
      }

      if (isRouteOneInputField) {
        event.target.name === ROUTE_ONE_SEARCH_FIELD_NAME
          ? setRouteOneSearchCursorData({ field: event.target, cursorPosition: event.target.selectionStart })
          : handleGetRouteOneDataWithDelay(true);
      }

      if (Object.keys(dealSourceData || {}).length > 0) {
        const deleteRouteOneVehicleResponse =
          dealSourceData?.vehicle_exists && dealSourceData?.manually_created
            ? dispatch(deleteUserVehicleThunk({ id: dealSourceData.vehicle_id }))
            : Promise.resolve();

        sessionStorage.removeItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG);

        if (isRac) {
          dispatch(resetRACDealSourceRelatedData());
        } else {
          dispatch(resetVehiclesByParams());
          dispatch(resetVehiclesByStock());
          dispatch(resetDealSourceData());
          dispatch(resetDealSourceDataFlags());
        }

        resetInputs();
        await deleteRouteOneVehicleResponse;
      }

      if (!isRouteOneDealerField && !(isRouteOneInputField && event?.type === 'change')) {
        trigger(ROUTE_ONE_FILEDS_NAMES);
      }

      getRouteOneDataFunction();
    },
    [
      isModalLoanDialogActive,
      handleGetRouteOneDataWithDelay,
      fetchRouteOneData,
      dealSourceData,
      dispatch,
      isRac,
      resetInputs,
      trigger,
      getValues,
      setValue,
      dealersList,
    ],
  );

  useEffect(() => {
    dispatch(getControlImagesThunk());

    if (isGamificationEnabled) {
      dispatch(fetchSummaryThunk());
    }
  }, [dispatch, isGamificationEnabled]);

  useEffect(() => {
    if (isSendRequest && !dealSourceDataFlags?.isGetInvalid && dealSourceDataFlags?.isReceived) {
      setIsSendRequest(false);
      dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));
    }
  }, [dispatch, isSendRequest, dealSourceDataFlags?.isGetInvalid, dealSourceDataFlags?.isReceived]);

  useEffect(() => {
    if (user.is_sso && (!user.isWithInventory || dealersList.length > 0)) {
      const { routeOneSearch, routeOneDealer, routeOneDealerId } = getValues();

      if (!routeOneSearch) {
        const searchFieldValue = isDealerTrack ? dealSourceData?.conversation_id : user.originalConversationId;

        setValue(ROUTE_ONE_SEARCH_FIELD_NAME, searchFieldValue ?? '');
      }

      if (user.isWithInventory && !routeOneDealer) {
        const dealer = dealersList.find((dealer) => dealer.route_one_id === user.originalExternalDealerId);

        if (dealer) {
          setValue(ROUTE_ONE_DEALER_FIELD_NAME, { value: dealer?.route_one_id, title: dealer?.name });
        }
      } else if (!user.isWithInventory && !routeOneDealerId) {
        setValue(ROUTE_ONE_DEALER_ID_FIELD_NAME, user.originalExternalDealerId ?? '');
      }
    }
  }, [
    getValues,
    setValue,
    user.isWithInventory,
    dealersList,
    user.is_sso,
    user.originalConversationId,
    user.originalExternalDealerId,
    isDealerTrack,
    dealSourceData?.conversation_id,
  ]);

  const mapModalByFieldName = useMemo(
    () =>
      new Map<string, ReactNode>([
        [PROBABILITY_ESTIMATOR_FIELDS.NET_DOWN_PAYMENT, <CalculatorAdornment dataTestId="net-down-payment-button" />],
        [
          PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME,
          <CalculatorAdornment dataTestId="income-calculator-button" />,
        ],
        [
          APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS,
          <CalculatorAdornment dataTestId="fron-and-back-ends-adds-button" />,
        ],
      ]),
    [],
  );

  const mapAdornmentOnClickByFieldName = useMemo(
    () =>
      new Map<string, Function>([
        [PROBABILITY_ESTIMATOR_FIELDS.NET_DOWN_PAYMENT, handleNetDownPaymentDialogOpen],
        [PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME, handleIncomeDialogOpen],
        [APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, handleFrontAndBackEndsAddsDialogOpen],
        [ROUTE_ONE_SEARCH_FIELD_NAME, dealSourceFieldHandleChange],
        [ROUTE_ONE_DEALER_ID_FIELD_NAME, dealSourceFieldHandleChange],
      ]),
    [
      handleNetDownPaymentDialogOpen,
      handleIncomeDialogOpen,
      handleFrontAndBackEndsAddsDialogOpen,
      dealSourceFieldHandleChange,
    ],
  );

  const mapOnClickFunctionByFieldName = useMemo(
    () =>
      new Map<string, VoidFunction>([
        [PROBABILITY_ESTIMATOR_FIELDS.NET_DOWN_PAYMENT, handleNetDownPaymentDialogOpen],
        [APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, handleFrontAndBackEndsAddsDialogOpen],
      ]),
    [handleNetDownPaymentDialogOpen, handleFrontAndBackEndsAddsDialogOpen],
  );

  const mapOnClickFunctionByFieldNameForRacPage = useMemo(
    () =>
      new Map<string, VoidFunction>([
        [PROBABILITY_ESTIMATOR_FIELDS.NET_DOWN_PAYMENT, handleNetDownPaymentDialogOpen],
        [APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, handleFrontAndBackEndsAddsDialogOpen],
      ]),
    [handleNetDownPaymentDialogOpen, handleFrontAndBackEndsAddsDialogOpen],
  );

  const { grossMonthlyIncome, creditScore, tier, routeOneSearch } = watch();

  const informationBlockNonRouteOneFieldsDisabled = useMemo(() => {
    // Always disable while vehicle table is loading
    if (isVehicleTableLoading) return true;

    // Disable when non-dealer SSO user clears the RouteOne conversation ID field
    if (user.is_sso && !isDealerSsoType && toShowDealSourceSearchFields && !routeOneSearch) return true;

    // Disable when RouteOne search is active but deal source data hasn't loaded yet
    if (toShowDealSourceSearchFields && !!routeOneSearch && isDealSourceDataEmpty) return true;

    return false;
  }, [
    isVehicleTableLoading,
    user.is_sso,
    isDealerSsoType,
    toShowDealSourceSearchFields,
    routeOneSearch,
    isDealSourceDataEmpty,
  ]);

  const getTierOrCreditScoreByPageName = useMemo(
    () =>
      new Map<string, any>([
        [PageName.PROBABILITY, creditScore],
        [PageName.LENDER, tier],
        [PageName.RAC, tier],
      ]),
    [creditScore, tier],
  );

  const monthlyPaymentSliderData = getTierOrCreditScoreByPageName.get(pageName);

  const handleChange = useCallback(() => {
    const { grossMonthlyIncome: currentGrossMonthlyIncome } = watch();

    if (isLender || isRac) {
      setValue(COMMON_MONTHLY_INCOME_MODAL, {
        income: {
          ...LENDERS_MOTHLY_INCOME_DEFAULT_VALUES.income,
          primaryMonthlyIncome: currentGrossMonthlyIncome,
          totalIncome: currentGrossMonthlyIncome || 0,
        },
      });
    } else {
      const otherCardData = {
        category: CATEGORIES.OTHER,
        type: TYPES.NON_CALCULATED,
        amountReceived: currentGrossMonthlyIncome,
      };

      setValue(FORM_FIELD_NAMES.TOTAL_INCOME, currentGrossMonthlyIncome);
      setValue(FORM_FIELD_NAMES.SAVED_INCOMES, [otherCardData]);

      dispatch(setIncome({ savedIncomes: [otherCardData], totalIncome: Number(currentGrossMonthlyIncome) }));
    }
  }, [dispatch, setValue, watch, setIncome, isLender, isRac]);

  const salesTaxRateHandleChange = useCallback(
    (event) => {
      const value = String(event.target.value);

      if (user?.is_sso && !user?.isWithInventory && isRac && value.charAt(value.length - 1) !== '.') {
        const values = getValues();

        const params = {
          ratesheet: values.rateSheetSettingsModal.rateSheet,
          region: values.rateSheetSettingsModal.region,
          tax_rate: values.feesAndTaxes.salesTaxRate,
        };

        vehicleApi.saveRateSheetsData(params);
      }
    },
    [user?.is_sso, user?.isWithInventory, isRac, getValues],
  );

  const estimatedFeesHandleChange = useCallback(() => {
    if (isMakeItWork) {
      const values = getValues();

      if (Object.keys(dealSourceData || {}).length > 0) {
        const initialEstimatedFees =
          (dealSourceData?.taxes_fees.sales_fees || 0) + (dealSourceData?.taxes_fees.title_reg_license || 0);

        if (initialEstimatedFees !== values.feesAndTaxes.estimatedFees) {
          setValue(ESTIMATED_FEES_OVERRIDDEN, true);
        }
      }
    }
  }, [getValues, isMakeItWork, dealSourceData, setValue]);

  const mapOnChangeCallbackByFieldName = useMemo(
    () =>
      new Map<string, Function>([
        [PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME, handleChange],
        [FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE, salesTaxRateHandleChange],
        [FEES_AND_TAXES_FIELD_NAMES.ESTIMATED_FEES, estimatedFeesHandleChange],
        [ROUTE_ONE_SEARCH_FIELD_NAME, dealSourceFieldHandleChange],
        [ROUTE_ONE_DEALER_FIELD_NAME, dealSourceFieldHandleChange],
        [ROUTE_ONE_DEALER_ID_FIELD_NAME, dealSourceFieldHandleChange],
      ]),
    [estimatedFeesHandleChange, handleChange, salesTaxRateHandleChange, dealSourceFieldHandleChange],
  );

  const handleDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        cancelSubmitDebounced();
      } else if (!hasBlockingModalOpen) {
        submitCallback();
      }
    },
    [cancelSubmitDebounced, submitCallback, hasBlockingModalOpen],
  );

  const generateFields = useCallback(
    (fields: IField[]) =>
      fields.map(({ name, component: Component, controlProps, label, isVerticalAlign }: IField) => {
        const getRACAndLenderClickCallback = (name: string) => {
          if (isRac && user.is_sso && !isDealerSsoType && isModalsDisabled[DisabledModals.REHASH_CALCULATOR]) {
            return undefined;
          }

          return mapOnClickFunctionByFieldNameForRacPage.get(name);
        };

        const changeCallback = mapOnChangeCallbackByFieldName.get(name);
        const adornment = mapModalByFieldName.get(name);
        const adornmentOnClick =
          isRac &&
          user.is_sso &&
          !isDealerSsoType &&
          isModalsDisabled[DisabledModals.REHASH_CALCULATOR] &&
          toShowDealSourceSearchFields &&
          ![ROUTE_ONE_SEARCH_FIELD_NAME, ROUTE_ONE_DEALER_ID_FIELD_NAME].includes(name)
            ? undefined
            : mapAdornmentOnClickByFieldName.get(name);

        const setAdornment = (fieldName: string) => {
          if (!isSmallWidth || (isSmallWidth && fieldName !== PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME))
            return adornment;

          return undefined;
        };

        const fieldWidth = controlProps?.fieldWidth ? { fieldWidth: controlProps.fieldWidth as ColumnWidth } : {};
        const clickCallback =
          isRac || isLender ? getRACAndLenderClickCallback(name) : mapOnClickFunctionByFieldName.get(name);

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
          // Check if the key pressed is Enter (key code 13)
          if (event.keyCode === 13) {
            event.preventDefault();
            dealSourceFieldHandleChange(event);
          }
        };

        if ((isLender && name === TIER_NAME) || (isRac && name === APPROVED_LOAN_TERMS_FIELD_NAMES.TIER)) {
          controlProps = { ...controlProps, isMakeItWork: isMakeItWork };
        }

        return (
          <FormRow
            {...fieldWidth}
            key={name}
            required={!!controlProps?.required}
            label={label}
            isVerticalAlign={isVerticalAlign}
            name={isRac || isLender ? name : undefined}
            boldLabel={!!controlProps?.boldLabel}
            control={
              <div className={name === ROUTE_ONE_SEARCH_FIELD_NAME ? 'position-relative' : undefined}>
                <Component
                  {...controlProps}
                  name={name}
                  endAdornment={setAdornment(name) || controlProps?.endAdornment}
                  endAdornmentOnClick={isVehicleTableLoading ? undefined : adornmentOnClick}
                  changeCallback={changeCallback as VoidFunction}
                  testId={name}
                  clickCallback={clickCallback}
                  handleEnterKeyDown={name === ROUTE_ONE_SEARCH_FIELD_NAME ? handleKeyDown : undefined}
                  disabled={informationBlockNonRouteOneFieldsDisabled && !ROUTE_ONE_FILEDS_NAMES.includes(name)}
                  onToggle={handleDropdownToggle}
                />
                {name === ROUTE_ONE_SEARCH_FIELD_NAME && (
                  <RouteOneAppTooltip id={name}>
                    {t('components.lender.routeOne.searchField.tooltip')}
                  </RouteOneAppTooltip>
                )}
              </div>
            }
          />
        );
      }),
    [
      mapOnChangeCallbackByFieldName,
      mapModalByFieldName,
      isRac,
      user.is_sso,
      isDealerSsoType,
      isLender,
      isModalsDisabled,
      mapAdornmentOnClickByFieldName,
      mapOnClickFunctionByFieldName,
      t,
      mapOnClickFunctionByFieldNameForRacPage,
      isSmallWidth,
      dealSourceFieldHandleChange,
      isMakeItWork,
      toShowDealSourceSearchFields,
      informationBlockNonRouteOneFieldsDisabled,
      isVehicleTableLoading,
      handleDropdownToggle,
    ],
  );

  useEffect(() => {
    if (routeOneSearchCursorData) {
      const { field, cursorPosition } = routeOneSearchCursorData;

      field.setSelectionRange(cursorPosition, cursorPosition);
      setRouteOneSearchCursorData(undefined);
    }
  }, [routeOneSearchCursorData]);

  useEffect(() => {
    const { routeOneSearch, routeOneDealer } = getValues();

    if (
      isLender &&
      !isShopByCallbackMode &&
      !isShopByBudgetMode &&
      !user.is_sso &&
      !!routeOneSearch &&
      !!routeOneDealer?.value
    ) {
      fetchRouteOneData();
    }

    if (user?.is_sso && !dealSourceDataFlags?.isReceived && toShowDealSourceSearchFields) {
      dispatch(setIsModalsDisabled({ value: true, key: DisabledModals.REHASH_CALCULATOR }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstFields = useMemo(() => generateFields(firstConsumerFields), [generateFields, firstConsumerFields]);

  const secondFields = useMemo(() => {
    if (secondConsumerFields.length > 0) {
      return generateFields(secondConsumerFields);
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondConsumerFields.length, generateFields]);

  const thirdFields = useMemo(() => {
    if (thirdConsumerFields.length > 0) return generateFields(thirdConsumerFields);
    else return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdConsumerFields.length, generateFields]);

  const fieldsRows = useMemo(() => {
    const maxLength = Math.max(firstFields.length, secondFields.length, thirdFields.length);
    const rows: (React.ReactNode | null)[][] = [];

    for (let i = 0; i < maxLength; i++) {
      const firstField = firstFields[i] ?? null;
      const secondField = secondFields[i] ?? null;
      const thirdField = thirdFields[i] ?? null;

      if (firstField || secondField || thirdField) {
        if (isShopByCallbackMode) {
          rows.push([thirdField, firstField, secondField]);
        } else {
          rows.push([firstField, secondField, thirdField]);
        }
      }
    }

    return rows;
  }, [firstFields, secondFields, thirdFields, isShopByCallbackMode]);

  const enhancedTitle = useMemo(() => {
    if (!toShowDealSourceSearchFields) {
      return title;
    }

    return (
      <>
        {title}
        <DealSourceHeaderFields
          toShowDealSourceSearchFields={toShowDealSourceSearchFields}
          dealSourceFieldHandleChange={dealSourceFieldHandleChange}
          dealSourceDataFlags={dealSourceDataFlags!}
          dealSourceConversationId={dealSourceData?.conversation_id}
          isDealSourceDataEmpty={isDealSourceDataEmpty}
          isDebouncing={isDebouncing}
          isVehicleTableLoading={isVehicleTableLoading}
        />
      </>
    );
  }, [
    title,
    toShowDealSourceSearchFields,
    dealSourceFieldHandleChange,
    dealSourceDataFlags,
    dealSourceData?.conversation_id,
    isDealSourceDataEmpty,
    isDebouncing,
    isVehicleTableLoading,
  ]);

  const headerComponent = useMemo(
    () => (
      <HeaderButtonsGroup isRac={isRac || isLender}>
        {isLender && (
          <IncomeCalculatorIcon
            iconUrl={STATIC_IMAGE_URLS.icons.incomeCalculator}
            data-testid="income-calculator-lender"
            onClick={handleLenderIncomeCalculatorDialogOpen}
            marginRightSpace
            disabled={isVehicleTableLoading}
          />
        )}
        {!isRac && !isLender && (
          <ButtonIcon
            iconUrl={STATIC_IMAGE_URLS.icons.print}
            data-testid="print-income"
            onClick={onButtonPrintClick}
            disabled={!grossMonthlyIncome || isVehicleTableLoading}
            marginRightSpace
          />
        )}
        {!isRac && settingsModalHandleOpen && (
          <>
            <ButtonIcon
              iconUrl={STATIC_IMAGE_URLS.icons.settings}
              data-testid="settings"
              disabled={settingsModalDisabled || isVehicleTableLoading}
              onClick={settingsModalHandleOpen}
              marginRightSpace
              data-tooltip-id="advanced-search-icon"
            />
            <DefaultTooltip id="advanced-search-icon" place="bottom-start" delayShow={200}>
              {t('components.preferences.advancedSearch')}
            </DefaultTooltip>
          </>
        )}
        <Button
          width="55px"
          title={t('components.approvedLoanInput.approvedLoanTerms.buttons.reset')}
          onClick={resetAll}
          buttonTheme={ButtonVariant.OUTLINE}
          disabled={isResetButtonDisabled}
        />
        {settingsModalHandleOpen && settingsModalIsActive && (
          <SettingsModal tableName={tableName} onClose={submitCallback} />
        )}
      </HeaderButtonsGroup>
    ),
    [
      isRac,
      isLender,
      onButtonPrintClick,
      grossMonthlyIncome,
      settingsModalHandleOpen,
      submitCallback,
      settingsModalDisabled,
      t,
      isResetButtonDisabled,
      settingsModalIsActive,
      tableName,
      handleLenderIncomeCalculatorDialogOpen,
      resetAll,
      isVehicleTableLoading,
    ],
  );

  const { S, M, XL, XXL, XXS, FULL, XS } = ColumnWidth;

  const hasAdvancedOverrides = (advancedOverridesFields?.length ?? 0) > 0;
  const advancedOverrideFieldsForPanel = useMemo(() => {
    if (!advancedOverridesFields?.length) return advancedOverridesFields;

    return advancedOverridesFields.map((field) => ({
      ...field,
      controlProps: {
        ...field.controlProps,
        disabled: informationBlockNonRouteOneFieldsDisabled || !!field.controlProps?.disabled,
      },
    }));
  }, [advancedOverridesFields, informationBlockNonRouteOneFieldsDisabled]);
  const hasMultipleColumns = thirdFields.length > 0 || secondFields.length > 0;

  return (
    <ConsumerInformationWrapper>
      <Row>
        <Col md={FULL} lg={showSideColumn ? XL : FULL} xl={showSideColumn ? XXL : FULL}>
          <Panel title={enhancedTitle} headerComponent={headerComponent}>
            <Row>
              <Col md={hasMultipleColumns ? M : hasAdvancedOverrides ? XS : M} xs={FULL}>
                {fieldsRows.map((row, rowIndex) => {
                  const columnCount =
                    (firstFields.length > 0 ? 1 : 0) +
                    (secondFields.length > 0 ? 1 : 0) +
                    (thirdFields.length > 0 ? 1 : 0);

                  let colWidth = FULL;

                  if (columnCount === 3) colWidth = XS;
                  else if (columnCount === 2) colWidth = M;

                  return (
                    <React.Fragment key={rowIndex}>
                      <Row className="d-flex align-items-end consumer-information-row">
                        {row.map((cell, colIndex) => (
                          <Col key={colIndex} xs={FULL} md={colWidth} lg={colWidth}>
                            {cell ?? null}
                          </Col>
                        ))}
                      </Row>
                    </React.Fragment>
                  );
                })}
              </Col>
              <>
                <Col
                  xs={FULL}
                  md={secondFields.length > 0 ? FULL : hasAdvancedOverrides ? XS : M}
                  lg={hasAdvancedOverrides ? S : M}
                  className={`${
                    secondFields.length > 0 ? 'rac-padding' : ''
                  } pr-0 d-flex flex-column justify-content-center`}
                >
                  <MonthlyPaymentSlider
                    name={PROBABILITY_ESTIMATOR_FIELDS.DESIRED_MONTHLY_PAYMENT}
                    fetchingVehicleFlagsSelector={fetchingVehicleFlagsSelector}
                    creditScore={monthlyPaymentSliderData}
                    income={grossMonthlyIncome as number}
                    disabled={!isFormValid || !areRequiredFieldsFilledIn || isVehicleTableLoading}
                    isRouteOneFlow={isRouteOneFlow}
                  />
                </Col>
                {advancedOverrideFieldsForPanel && advancedOverrideFieldsForPanel.length > 0 && (
                  <Col xs={FULL} md={XXS}>
                    <OverrideFieldsWrapper>
                      <PanelWithFields
                        title={t('components.preferences.advancedOverrides')}
                        fieldsConfig={advancedOverrideFieldsForPanel}
                      />
                    </OverrideFieldsWrapper>
                  </Col>
                )}
              </>
              {isModalNetDownPaymentDialogActive && (
                <NetDownPaymentModal
                  onHide={submitCallback}
                  isLender={isRac || isLender}
                  fieldWidth={M}
                  isRouteOneFlow={isRouteOneFlow}
                />
              )}
              {isFrontAndBackEndsAddsDialogActive && (
                <FrontAndBackEndsAddsModal isLender={isLender} onClose={submitCallback} />
              )}
              {isRac || isLender ? (
                <LendersMonthlyIncomeModal
                  show={isIncomeDialogActive}
                  onHide={() => {
                    handleIncomeDialogClose()();
                    submitCallback();
                  }}
                  initialValues={values.lendersMonthlyIncomeModal}
                  modalName={COMMON_MONTHLY_INCOME_MODAL}
                  totalName={PROBABILITY_ESTIMATOR_FIELDS.GROSS_MONTHLY_INCOME}
                  isLender={isLender}
                  isDealSourceData={isMakeItWork && Object.keys(dealSourceData || {}).length > 0}
                  dealSourceData={dealSourceData!}
                />
              ) : (
                <IncomeCalculator
                  show={isIncomeDialogActive}
                  onHide={() => {
                    handleIncomeDialogClose()();
                    submitCallback();
                  }}
                  onIncomeCalculatorSubmit={onIncomeCalculatorSubmit}
                  incomesSelector={getIncome}
                />
              )}
              {isLender && (
                <IncomeCalculator
                  show={isLenderIncomeCalculatorDialogActive}
                  onHide={() => {
                    handleLenderIncomeCalculatorDialogClose()();
                    submitCallback();
                  }}
                  onIncomeCalculatorSubmit={onIncomeCalculatorSubmit}
                  incomesSelector={getIncome}
                  showPrintButton
                />
              )}
            </Row>
          </Panel>
        </Col>
        {showSideColumn && (
          <Col
            lg={XS}
            xl={XXS}
            className="d-none d-lg-block"
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '0.375rem',
            }}
          >
            <AchievementProgressCard
              fallbackContent={
                sideImageLogo ? (
                  <SideImageContainer
                    onClick={handleMainPageLogoClick}
                    style={{
                      cursor: sideImageLogoShouldDisplayUpgrade || sideImageLogoLink ? 'pointer' : 'default',
                    }}
                  >
                    <img src={sideImageLogo} alt="" />
                    {(sideImageLogoShouldDisplayUpgrade || sideImageLogoLink) && (
                      <LinkOutIconWrapper>
                        <LinkOutIcon src={STATIC_IMAGE_URLS.icons.externalLink} alt="" />
                      </LinkOutIconWrapper>
                    )}
                  </SideImageContainer>
                ) : null
              }
            />
          </Col>
        )}
      </Row>
    </ConsumerInformationWrapper>
  );
};
